import { useTranslation } from '@thoughtspot/i18n';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, } from 'react';
import { useBlinkContextForSparkflow } from "../../../contexts/sparkflow-blink-context";
import { getSparkflowConfig, setSparkflowConfig, } from "../../../contexts/sparkflow-context";
import { useSparkflowNavService } from "../../../services/nav-service/nav-service";
import { create } from "../../../utils/logger";
import { postSparkflowChat } from "../../../utils/sparkflow-api";
import { BUILD_PHASE, DEFAULT_FUNCTION_ID, DEFAULT_INDUSTRY_ID, getFunctionOptions, getIndustryOptions, getSSEStreamUrl, OTHER_FUNCTION_ID, OTHER_INDUSTRY_ID, } from "../../../utils/sparkflow-constants";
import { useSSEStream } from "../../../utils/sse-client";
import { buildReducer, formatColumnLabel, initialBuildState, } from '../utils/build-helpers';
import { useSimulatedProgress } from './use-simulated-progress';
const logger = create('sparkflow-build');
export function useSpotbuild() {
    const { t } = useTranslation();
    const { navService, sessionService } = useBlinkContextForSparkflow();
    const sparkflowNavService = useSparkflowNavService();
    const { connect, disconnect } = useSSEStream();
    const sparkflowConfig = getSparkflowConfig();
    const sparkflowDispatchers = setSparkflowConfig();
    const onboardingMTEnabled = sessionService.isOnboardingMTServicesEnabled();
    const [buildErrorMessage, setBuildErrorMessage] = useState('');
    const [progressResetKey, setProgressResetKey] = useState(0);
    const clearBuildError = useCallback(() => {
        setBuildErrorMessage('');
    }, []);
    const userName = sessionService?.getUserName();
    const orgId = sessionService?.getCurrentOrgId();
    const hasValidOnboarding = !!sparkflowConfig?.onboardingSelections?.selectedFunction &&
        !!sparkflowConfig?.onboardingSelections?.selectedIndustry;
    const selectedFunctionId = sparkflowConfig?.onboardingSelections?.selectedFunction ?? '';
    const selectedIndustryId = sparkflowConfig?.onboardingSelections?.selectedIndustry ?? '';
    const effectiveFunctionId = selectedFunctionId === OTHER_FUNCTION_ID
        ? DEFAULT_FUNCTION_ID
        : selectedFunctionId;
    const effectiveIndustryId = selectedIndustryId === OTHER_INDUSTRY_ID
        ? DEFAULT_INDUSTRY_ID
        : selectedIndustryId;
    const functionLabel = getFunctionOptions().find(opt => opt.id === effectiveFunctionId)
        ?.label || '';
    const industryLabel = selectedIndustryId === OTHER_INDUSTRY_ID
        ? getIndustryOptions().find(opt => opt.id === effectiveIndustryId)
            ?.label || ''
        : '';
    useEffect(() => {
        if (!hasValidOnboarding) {
            navService.goToSparkflowOnboarding();
        }
    }, [hasValidOnboarding, navService]);
    const [buildState, dispatch] = useReducer(buildReducer, initialBuildState);
    const { phase: buildPhase, previewData } = buildState;
    const simulatedProgress = useSimulatedProgress(buildPhase, !!buildErrorMessage, progressResetKey);
    const columns = useMemo(() => {
        if (!previewData || previewData.length === 0) {
            return [];
        }
        return Object.keys(previewData[0]).map(key => ({
            id: key,
            label: formatColumnLabel(key),
        }));
    }, [previewData]);
    const chatSentRef = useRef('');
    const chatAbortControllerRef = useRef(null);
    const getPrompt = useCallback(() => {
        if (!hasValidOnboarding)
            return '';
        return t('sparkflow.onboarding.chat.prompt', {
            function: effectiveFunctionId,
            industry: effectiveIndustryId,
        });
    }, [effectiveFunctionId, effectiveIndustryId, hasValidOnboarding]);
    const trySendChat = useCallback((clientId) => {
        if (chatSentRef.current === clientId)
            return;
        chatAbortControllerRef.current?.abort();
        if (chatSentRef.current)
            setProgressResetKey(k => k + 1);
        chatSentRef.current = clientId;
        const controller = new AbortController();
        chatAbortControllerRef.current = controller;
        postSparkflowChat({
            prompt: getPrompt(),
            app_id: clientId,
            org_id: orgId?.toString() ?? '',
            user_id: userName ?? '',
            function: effectiveFunctionId,
            industry: effectiveIndustryId,
        }, onboardingMTEnabled, controller.signal)
            .then(response => {
            if (response.ok) {
                return response
                    .json()
                    .then((data) => {
                    sparkflowDispatchers.updateBuildInfo({
                        liveboardId: data.liveboardId,
                        modelId: data.modelId,
                        tableId: data.tableId,
                        industry: selectedIndustryId,
                        function: selectedFunctionId,
                    });
                    return undefined;
                })
                    .catch(error => {
                    logger.error('Failed to parse chat response:', error);
                    setBuildErrorMessage(error?.message ||
                        t('error.validation.default'));
                });
            }
            logger.error('Failed to send chat:', response.statusText);
            setBuildErrorMessage(response.statusText || t('error.validation.default'));
            return undefined;
        })
            .catch(error => {
            if (error?.name === 'AbortError')
                return;
            logger.error('Failed to send chat:', error);
            setBuildErrorMessage(error?.message || t('error.validation.default'));
        });
    }, [getPrompt, orgId, userName, sparkflowDispatchers]);
    const dispatchBuildEvent = useCallback((event) => {
        const { progress: eventProgress, previewData, status } = event.data;
        dispatch({ type: 'START_BUILDING' });
        if (typeof eventProgress === 'number') {
            dispatch({ type: 'SET_PROGRESS', progress: eventProgress });
        }
        if (status && status === BUILD_PHASE.GENERATING_PREVIEW) {
            dispatch({ type: 'GENERATING_PREVIEW' });
        }
        if (previewData && previewData.length > 0) {
            dispatch({ type: 'PREVIEW_READY', previewData });
        }
        if (status && status === BUILD_PHASE.COMPLETED) {
            dispatch({ type: 'BUILD_COMPLETE' });
            disconnect();
        }
    }, [disconnect]);
    const handleSSEEvents = useCallback((events) => {
        events.forEach(event => {
            dispatchBuildEvent(event);
            if (event.data.clientId) {
                setBuildErrorMessage('');
                trySendChat(event.data.clientId);
            }
        });
    }, [dispatchBuildEvent, trySendChat]);
    useEffect(() => {
        chatSentRef.current = '';
        connect({ url: getSSEStreamUrl(onboardingMTEnabled) }, {
            onEvent: handleSSEEvents,
            onError: sseError => {
                logger.error('SSE error:', sseError);
                setBuildErrorMessage(sseError?.message || t('error.validation.default'));
            },
            onComplete: () => {
                logger.log('SSE stream completed');
            },
        });
        return () => disconnect();
    }, [connect, disconnect]);
    const navigateToOnboarding = useCallback(() => {
        disconnect();
        chatAbortControllerRef.current?.abort();
        chatAbortControllerRef.current = null;
        navService.goToSparkflowOnboarding();
    }, [disconnect, navService]);
    const navigateToAgentSelection = useCallback(() => {
        sparkflowNavService.goToAgentSelection();
    }, [sparkflowNavService]);
    return {
        buildPhase,
        progress: simulatedProgress,
        previewData,
        columns,
        liveboardId: sparkflowConfig?.buildInfo?.liveboardId ?? '',
        modelId: sparkflowConfig?.buildInfo?.modelId ?? '',
        tableId: sparkflowConfig?.buildInfo?.tableId ?? '',
        industry: sparkflowConfig?.buildInfo?.industry ?? '',
        function: sparkflowConfig?.buildInfo?.function ?? '',
        buildErrorMessage,
        clearBuildError,
        functionLabel,
        industryLabel,
        navigateToOnboarding,
        navigateToAgentSelection,
    };
}
//# sourceMappingURL=use-spotbuild.js.map